import React, { useEffect, useMemo, useState } from "react";
import {
  EnvelopeSimple, EnvelopeSimpleOpen, MagnifyingGlass, Trash, Copy,
  Phone, Clock, ArrowsClockwise, CaretDown, CaretUp, CheckCircle, WarningCircle, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

function deliveryStats(item) {
  const delivered = Array.isArray(item.delivered_to) ? item.delivered_to : [];
  const errors = Array.isArray(item.delivery_errors) ? item.delivery_errors : [];
  const total = delivered.length + errors.length;
  // Older contacts (pre-Resend wiring) have no delivery fields — show nothing.
  if (total === 0 && !item.delivered_email) return null;
  return { delivered, errors, total, ok: delivered.length };
}

function DeliveryChip({ stats, compact = false }) {
  if (!stats) return null;
  const { ok, total } = stats;
  const allGood = total > 0 && ok === total;
  const partial = ok > 0 && ok < total;
  const noneOk = ok === 0 && total > 0;
  let cls = "text-zinc-500 border-ink-600 bg-ink-800";
  let Icon = WarningCircle;
  if (allGood) { cls = "text-emerald-400 border-emerald-400/30 bg-emerald-500/10"; Icon = CheckCircle; }
  else if (partial) { cls = "text-amber-300 border-amber-400/30 bg-amber-500/10"; Icon = WarningCircle; }
  else if (noneOk) { cls = "text-neon-magenta border-neon-magenta/30 bg-neon-magenta/10"; Icon = XCircle; }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 ${compact ? "py-0" : "py-0.5"} text-[10px] font-mono uppercase tracking-[0.15em] ${cls}`}
      title={allGood ? "Forwarded to all management emails" : partial ? "Some deliveries failed" : "No deliveries succeeded"}
    >
      <Icon size={10} weight="fill" /> {ok}/{total}
    </span>
  );
}

function Row({ item, expanded, onToggle, onRead, onDelete, onCopyEmail }) {
  const unread = !item.read;
  const stats = deliveryStats(item);
  return (
    <li className={`border-b border-ink-700/60 last:border-0 ${unread ? "bg-neon-cyan/[0.03]" : ""}`} data-testid={`inbox-row-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-ink-800/40 transition-colors"
        data-testid={`inbox-toggle-${item.id}`}
      >
        {unread ? (
          <EnvelopeSimple size={16} weight="fill" className="text-neon-cyan shrink-0" />
        ) : (
          <EnvelopeSimpleOpen size={16} className="text-zinc-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
          <div className="col-span-12 sm:col-span-3 truncate flex items-center gap-2">
            <span className={`text-sm ${unread ? "text-white font-semibold" : "text-zinc-300"} truncate`}>{item.name}</span>
            {stats && <span data-testid={`inbox-delivery-${item.id}`} className="shrink-0"><DeliveryChip stats={stats} compact /></span>}
          </div>
          <div className="col-span-8 sm:col-span-4 truncate text-xs font-mono text-zinc-400">{item.email}</div>
          <div className="hidden sm:block sm:col-span-3 truncate text-xs text-zinc-500">
            {item.message?.slice(0, 70)}{item.message?.length > 70 ? "…" : ""}
          </div>
          <div className="col-span-4 sm:col-span-2 text-right text-[10px] font-mono text-zinc-500 inline-flex items-center justify-end gap-1">
            <Clock size={12} /> {timeAgo(item.created_at)}
          </div>
        </div>
        {expanded ? <CaretUp size={14} className="text-zinc-500" /> : <CaretDown size={14} className="text-zinc-500" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3" data-testid={`inbox-detail-${item.id}`}>
          <div className="flex flex-wrap gap-2 text-xs">
            <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50"><EnvelopeSimple size={12}/> Reply</a>
            <button onClick={onCopyEmail} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50" data-testid={`inbox-copy-email-${item.id}`}><Copy size={12}/> Copy email</button>
            {item.phone && (
              <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50"><Phone size={12}/> {item.phone}</a>
            )}
            {item.read ? (
              <button onClick={() => onRead(false)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-amber-300 hover:border-amber-300/40" data-testid={`inbox-unread-${item.id}`}><EnvelopeSimple size={12}/> Mark unread</button>
            ) : (
              <button onClick={() => onRead(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/40" data-testid={`inbox-read-${item.id}`}><EnvelopeSimpleOpen size={12}/> Mark read</button>
            )}
            <button onClick={onDelete} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-magenta hover:border-neon-magenta/50 ml-auto" data-testid={`inbox-delete-${item.id}`}><Trash size={12}/> Delete</button>
          </div>
          {stats && (
            <div className="rounded-md border border-ink-700 bg-ink-900/60 p-3 space-y-2" data-testid={`inbox-delivery-detail-${item.id}`}>
              <div className="flex items-center gap-2 text-xs">
                <DeliveryChip stats={stats} />
                <span className="text-zinc-400 font-mono">
                  Forwarded to {stats.ok} of {stats.total} management email{stats.total === 1 ? "" : "s"}
                </span>
              </div>
              {stats.delivered.length > 0 && (
                <div className="text-[11px] text-zinc-400">
                  <span className="text-emerald-400 font-mono">✓ Delivered:</span>{" "}
                  <span className="font-mono break-all">{stats.delivered.join(", ")}</span>
                </div>
              )}
              {stats.errors.length > 0 && (
                <div className="text-[11px] text-zinc-400 space-y-1">
                  <div className="text-neon-magenta font-mono">✗ Failed:</div>
                  {stats.errors.map((e) => (
                    <div key={e.email} className="pl-3 font-mono break-all">
                      <span className="text-zinc-300">{e.email}</span>
                      <span className="text-zinc-600"> — {(e.reason || "").slice(0, 140)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="rounded-md bg-ink-900/70 border border-ink-700 p-3 text-sm text-zinc-200 whitespace-pre-wrap font-light leading-relaxed" data-testid={`inbox-message-${item.id}`}>
            {item.message}
          </div>
        </div>
      )}
    </li>
  );
}

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filter === "unread" && c.read) return false;
      if (!q) return true;
      return (c.name || "").toLowerCase().includes(q) ||
             (c.email || "").toLowerCase().includes(q) ||
             (c.message || "").toLowerCase().includes(q);
    });
  }, [items, filter, search]);

  const unreadCount = useMemo(() => items.filter((c) => !c.read).length, [items]);

  const toggle = (id) => {
    setOpenId((cur) => (cur === id ? null : id));
    // Auto-mark read when opening an unread item
    const item = items.find((i) => i.id === id);
    if (item && !item.read && openId !== id) markRead(id, true, { silent: true });
  };

  const markRead = async (id, read, opts = {}) => {
    try {
      const { data } = await api.patch(`/contact/${id}`, { read });
      setItems((prev) => prev.map((c) => c.id === id ? { ...c, ...data } : c));
      if (!opts.silent) toast.success(read ? "Marked read" : "Marked unread");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/contact/${id}`);
      setItems((prev) => prev.filter((c) => c.id !== id));
      if (openId === id) setOpenId(null);
      toast.success("Inquiry deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); toast.success("Email copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  return (
    <section data-testid="admin-inbox">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Inbox</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">
            Labels & Management <span className="text-zinc-500 font-mono text-base">({items.length}{unreadCount > 0 ? ` · ${unreadCount} unread` : ""})</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-8 pr-3 py-1.5 rounded-md bg-ink-900 border border-ink-600 text-white text-xs w-44 focus:outline-none focus:border-neon-cyan/60"
              data-testid="inbox-search"
            />
          </div>
          <div className="flex rounded-md border border-ink-600 overflow-hidden text-xs">
            <button onClick={() => setFilter("all")} className={`px-2.5 py-1.5 ${filter === "all" ? "bg-neon-cyan text-black font-semibold" : "text-zinc-400 hover:text-white"}`} data-testid="inbox-filter-all">All</button>
            <button onClick={() => setFilter("unread")} className={`px-2.5 py-1.5 ${filter === "unread" ? "bg-neon-cyan text-black font-semibold" : "text-zinc-400 hover:text-white"}`} data-testid="inbox-filter-unread">Unread</button>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="inbox-reload"><ArrowsClockwise size={14} /> Reload</button>
        </div>
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-zinc-500 text-sm" data-testid="inbox-empty">
            {items.length === 0 ? "No label inquiries yet. They'll appear here when labels submit the contact form." : "No inquiries match your search."}
          </div>
        ) : (
          <ul>
            {filtered.map((item) => (
              <Row
                key={item.id}
                item={item}
                expanded={openId === item.id}
                onToggle={() => toggle(item.id)}
                onRead={(r) => markRead(item.id, r)}
                onDelete={() => remove(item.id)}
                onCopyEmail={() => copyEmail(item.email)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
